import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';

interface PrivacyOption {
  id: string;
  title: string;
  description: string;
  required: boolean;
  defaultValue: boolean;
}

@Component({
  selector: 'app-privacy-choices',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    <section class="privacy-choices">
      <div class="container">
        <header class="page-header">
          <h1>Privacy Choices</h1>
          <p class="subtitle">Manage your privacy preferences and data settings</p>
        </header>

        <div class="content">
          <div class="section">
            <h2>Your Privacy Rights</h2>
            <p class="section-intro">
              You have the right to control how your personal information is collected and used.
              Use the settings below to customize your privacy preferences.
            </p>
          </div>

          <form [formGroup]="privacyForm" (ngSubmit)="onSubmit()" class="privacy-form">
            <div class="privacy-options">
              @for (option of privacyOptions; track option.id) {
                <div class="privacy-option">
                  <div class="option-header">
                    <div class="option-title">
                      <h3>{{ option.title }}</h3>
                      @if (option.required) {
                        <span class="required-badge">Required</span>
                      }
                    </div>
                    <div class="toggle-switch">
                      <input
                        type="checkbox"
                        [id]="option.id"
                        [formControlName]="option.id"
                        [disabled]="option.required"
                      >
                      <label [for]="option.id"></label>
                    </div>
                  </div>
                  <p class="option-description">{{ option.description }}</p>
                </div>
              }
            </div>

            <div class="data-portability">
              <h2>Data Portability</h2>
              <p>
                You can request a copy of your personal data or ask for it to be
                transferred to another service provider.
              </p>
              <div class="action-buttons">
                <button type="button" class="secondary-button" (click)="requestDataExport()">
                  Export My Data
                </button>
                <button type="button" class="secondary-button" (click)="requestDataTransfer()">
                  Transfer My Data
                </button>
              </div>
            </div>

            <div class="data-deletion">
              <h2>Data Deletion</h2>
              <p>
                You can request the deletion of your personal data. Please note that
                some data may be retained for legal or legitimate business purposes.
              </p>
              <button type="button" class="danger-button" (click)="requestDataDeletion()">
                Request Data Deletion
              </button>
            </div>

            <div class="form-actions">
              <button type="submit" class="primary-button" [disabled]="isSubmitting">
                {{ isSubmitting ? 'Saving...' : 'Save Preferences' }}
              </button>
              <button type="button" class="secondary-button" (click)="resetToDefaults()">
                Reset to Defaults
              </button>
            </div>
          </form>

          <div class="additional-info">
            <h2>Additional Information</h2>
            <div class="info-grid">
              <div class="info-card">
                <h3>Data Processing</h3>
                <p>
                  Learn more about how we process and protect your personal data in our
                  <a routerLink="/privacy-policy">Privacy Policy</a>.
                </p>
              </div>
              <div class="info-card">
                <h3>Cookie Usage</h3>
                <p>
                  For information about our use of cookies, please visit our
                  <a routerLink="/cookie-policy">Cookie Policy</a>.
                </p>
              </div>
              <div class="info-card">
                <h3>Contact Us</h3>
                <p>
                  If you have any questions about your privacy choices, please
                  <a routerLink="/contact">contact our Privacy Team</a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [`
    .privacy-choices {
      padding: 4rem 0;
      background: var(--background-light);
    }

    .container {
      max-width: 800px;
      margin: 0 auto;
      padding: 0 1rem;
    }

    .page-header {
      text-align: center;
      margin-bottom: 3rem;
    }

    h1 {
      font-size: 2.5rem;
      color: var(--text-primary);
      margin-bottom: 0.5rem;
    }

    .subtitle {
      font-size: 1.1rem;
      color: var(--text-secondary);
    }

    .content {
      background: white;
      padding: 2rem;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }

    .section {
      margin-bottom: 2rem;
    }

    h2 {
      font-size: 1.5rem;
      color: var(--text-primary);
      margin-bottom: 1rem;
    }

    .section-intro {
      color: var(--text-secondary);
      line-height: 1.6;
    }

    .privacy-options {
      display: grid;
      gap: 1.5rem;
      margin-bottom: 2rem;
    }

    .privacy-option {
      background: var(--background-light);
      padding: 1.5rem;
      border-radius: 8px;
    }

    .option-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;
    }

    .option-title {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    h3 {
      font-size: 1.25rem;
      color: var(--text-primary);
      margin: 0;
    }

    .required-badge {
      background: var(--primary-color-light);
      color: var(--primary-color);
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
      font-size: 0.875rem;
      font-weight: 500;
    }

    .option-description {
      color: var(--text-secondary);
      line-height: 1.6;
      margin: 0;
    }

    .toggle-switch {
      position: relative;
      width: 60px;
      height: 34px;
    }

    .toggle-switch input {
      opacity: 0;
      width: 0;
      height: 0;
    }

    .toggle-switch label {
      position: absolute;
      cursor: pointer;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: #ccc;
      transition: .4s;
      border-radius: 34px;
    }

    .toggle-switch label:before {
      position: absolute;
      content: "";
      height: 26px;
      width: 26px;
      left: 4px;
      bottom: 4px;
      background-color: white;
      transition: .4s;
      border-radius: 50%;
    }

    .toggle-switch input:checked + label {
      background-color: var(--primary-color);
    }

    .toggle-switch input:checked + label:before {
      transform: translateX(26px);
    }

    .toggle-switch input:disabled + label {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .data-portability,
    .data-deletion {
      margin-bottom: 2rem;
      padding: 1.5rem;
      background: var(--background-light);
      border-radius: 8px;
    }

    .action-buttons {
      display: flex;
      gap: 1rem;
      margin-top: 1rem;
    }

    .form-actions {
      display: flex;
      gap: 1rem;
      margin-bottom: 2rem;
      padding-top: 2rem;
      border-top: 1px solid var(--border-color);
    }

    .primary-button,
    .secondary-button,
    .danger-button {
      padding: 0.75rem 1.5rem;
      border-radius: 4px;
      font-size: 1rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s;
    }

    .primary-button {
      background: var(--primary-color);
      color: white;
      border: none;
    }

    .primary-button:hover:not(:disabled) {
      background: var(--primary-dark);
    }

    .primary-button:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .secondary-button {
      background: transparent;
      color: var(--text-primary);
      border: 1px solid var(--border-color);
    }

    .secondary-button:hover {
      background: var(--background-light);
    }

    .danger-button {
      background: var(--error-color);
      color: white;
      border: none;
    }

    .danger-button:hover {
      background: var(--error-dark);
    }

    .additional-info {
      margin-top: 3rem;
      padding-top: 2rem;
      border-top: 1px solid var(--border-color);
    }

    .info-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 1.5rem;
      margin-top: 1.5rem;
    }

    .info-card {
      background: var(--background-light);
      padding: 1.5rem;
      border-radius: 8px;
    }

    .info-card h3 {
      margin-bottom: 1rem;
    }

    .info-card p {
      color: var(--text-secondary);
      margin: 0;
    }

    .info-card a {
      color: var(--primary-color);
      text-decoration: none;
    }

    .info-card a:hover {
      text-decoration: underline;
    }

    @media (max-width: 768px) {
      .page-header {
        margin-bottom: 2rem;
      }

      h1 {
        font-size: 2rem;
      }

      .content {
        padding: 1.5rem;
      }

      .action-buttons,
      .form-actions {
        flex-direction: column;
      }

      .info-grid {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class PrivacyChoicesComponent {
  privacyForm!: FormGroup;
  isSubmitting = false;

  privacyOptions: PrivacyOption[] = [
    {
      id: 'essentialCookies',
      title: 'Essential Cookies',
      description: 'Required for basic website functionality and security features.',
      required: true,
      defaultValue: true
    },
    {
      id: 'analyticsTracking',
      title: 'Analytics & Performance',
      description: 'Help us understand how visitors interact with our website to improve user experience.',
      required: false,
      defaultValue: true
    },
    {
      id: 'marketingCookies',
      title: 'Marketing & Advertising',
      description: 'Allow us to deliver personalized advertisements and content based on your interests.',
      required: false,
      defaultValue: false
    },
    {
      id: 'thirdPartySharing',
      title: 'Third-Party Data Sharing',
      description: 'Share data with our trusted partners for service improvement and marketing purposes.',
      required: false,
      defaultValue: false
    },
    {
      id: 'communicationPreferences',
      title: 'Communication Preferences',
      description: 'Receive updates about our products, services, and industry insights.',
      required: false,
      defaultValue: true
    }
  ];

  constructor(private fb: FormBuilder) {
    this.initForm();
  }

  private initForm() {
    const formGroup: any = {};
    this.privacyOptions.forEach(option => {
      formGroup[option.id] = [option.defaultValue];
    });
    this.privacyForm = this.fb.group(formGroup);
  }

  async onSubmit() {
    if (this.privacyForm.valid) {
      this.isSubmitting = true;
      try {
        // Here you would typically make an API call to save preferences
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulated API call
        console.log('Privacy preferences saved:', this.privacyForm.value);
        // Show success message
      } catch (error) {
        console.error('Error saving preferences:', error);
        // Show error message
      } finally {
        this.isSubmitting = false;
      }
    }
  }

  resetToDefaults() {
    this.privacyOptions.forEach(option => {
      this.privacyForm.get(option.id)?.setValue(option.defaultValue);
    });
  }

  requestDataExport() {
    // Implement data export functionality
    console.log('Data export requested');
  }

  requestDataTransfer() {
    // Implement data transfer functionality
    console.log('Data transfer requested');
  }

  requestDataDeletion() {
    // Implement data deletion functionality
    console.log('Data deletion requested');
  }
} 